// Lecture du format texte d'import de tâches.
//
// Format, une tâche par ligne :
//   Nom de la tâche | début | échéance | priorité | responsable
// Seul le nom est obligatoire ; les champs suivants peuvent être omis.
//
// Sous la ligne d'une tâche :
//   - élément de checklist        (ou « * élément »)
//   > texte de description
//
// Les lignes vides et celles commençant par // sont ignorées, ce qui permet de
// garder des commentaires et des séparateurs de phase dans le fichier collé.

const PRIORITES = {
  urgente: 'Urgent',
  urgent: 'Urgent',
  haute: 'High',
  high: 'High',
  moyenne: 'Medium',
  medium: 'Medium',
  basse: 'Low',
  low: 'Low',
};

const STATUTS = {
  'à faire': 'Todo',
  'a faire': 'Todo',
  todo: 'Todo',
  'en cours': 'In_Progress',
  in_progress: 'In_Progress',
  revue: 'In_Review',
  in_review: 'In_Review',
  terminé: 'Done',
  termine: 'Done',
  done: 'Done',
  bloqué: 'Blocked',
  bloque: 'Blocked',
  blocked: 'Blocked',
};

/** Normalise « Haute » → « High ». Renvoie null si le mot n'est pas reconnu. */
function lirePriorite(mot) {
  if (!mot) return null;
  return PRIORITES[mot.trim().toLowerCase()] || null;
}

function lireStatut(mot) {
  if (!mot) return null;
  return STATUTS[mot.trim().toLowerCase()] || null;
}

/**
 * Analyse le texte collé et renvoie { taches, erreurs }.
 * Les erreurs sont des messages lisibles avec le numéro de ligne : c'est ce
 * qui permet à la personne de corriger sa liste sans deviner.
 */
export function analyserImport(texte) {
  const lignes = String(texte || '').split(/\r?\n/);
  const taches = [];
  const erreurs = [];

  lignes.forEach((brute, index) => {
    const numero = index + 1;
    const ligne = brute.trim();

    if (!ligne || ligne.startsWith('//')) return;

    // Élément de checklist
    if (/^[-*•]\s*/.test(ligne)) {
      const item = ligne.replace(/^[-*•]\s*/, '').trim();
      if (!item) return;
      if (taches.length === 0) {
        erreurs.push(`Ligne ${numero} : élément de checklist sans tâche au-dessus.`);
        return;
      }
      taches[taches.length - 1].checklist.push(item);
      return;
    }

    // Description
    if (ligne.startsWith('>')) {
      const texteDescription = ligne.slice(1).trim();
      if (!texteDescription) return;
      if (taches.length === 0) {
        erreurs.push(`Ligne ${numero} : description sans tâche au-dessus.`);
        return;
      }
      const derniere = taches[taches.length - 1];
      derniere.description = derniere.description
        ? `${derniere.description} ${texteDescription}`
        : texteDescription;
      return;
    }

    // Nouvelle tâche
    const champs = ligne.split('|').map((c) => c.trim());
    const nom = champs[0];
    if (!nom) {
      erreurs.push(`Ligne ${numero} : nom de tâche vide.`);
      return;
    }

    const priorite = champs[3] ? lirePriorite(champs[3]) : null;
    if (champs[3] && !priorite) {
      erreurs.push(
        `Ligne ${numero} : priorité « ${champs[3]} » inconnue (Urgente, Haute, Moyenne ou Basse).`
      );
    }

    const statut = champs[5] ? lireStatut(champs[5]) : null;
    if (champs[5] && !statut) {
      erreurs.push(`Ligne ${numero} : statut « ${champs[5]} » inconnu.`);
    }

    taches.push({
      ligne: numero,
      name: nom,
      start_date: champs[1] || '',
      due_date: champs[2] || '',
      priority: priorite || 'Medium',
      assigned_to: champs[4] || '',
      status: statut || 'Todo',
      description: '',
      checklist: [],
    });
  });

  return { taches, erreurs };
}

/** Exemple affiché dans la fenêtre, pour montrer le format sans documentation. */
export const EXEMPLE_IMPORT = `// Une tâche par ligne :
// Nom | début | échéance | priorité | responsable
Revue technologique de clarification | 29/07/2026 | 12/08/2026 | Haute | mohamed_saadi
> Comparer les technologies candidates et préparer l'arbitrage budgétaire.
- Note de base de conception
- Note de revue technologique
- Synthèse de dimensionnement

Bilans matière et énergie | 12/08/2026 | 26/08/2026 | Haute
- Rapport de bilan matière et énergie`;
